import React, { useContext, useEffect } from 'react';
import { Form } from 'react-final-form';
import services from 'services/services';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { dictionaryPlatformsTypes } from 'store/actions';

// Import helpers
import {
	createMaterialOptions,
	createInitialValues,
	convertAssets,
	MATERIAL_PLATFORM_TYPES
} from './helpers/index';

// Import components
import { Button, FormGroup, Col } from 'reactstrap';
import Fields from './fields/Fields';

// Import utilities
import { createFormNotification } from 'components/utilities/notifications/index';

// Import select options
import { select_types } from './fields/select_type_options';

// Validate
import validate from './validation';

// Import context
import { MaterialsContext } from './materials_context/MaterialsContext';

// Import translation
import { useTranslation } from 'react-i18next';

const MaterialsForm = ({
	resources: { id, type, assets },
	dictionaryPlatformsTypes,
	platformList
}) => {
	const {
		toggleFormModal,
		updateData,
		isEdit,
		toggleEdit,
		assetId,
		setAssetId
	} = useContext(MaterialsContext);

	const { t } = useTranslation();

	useEffect(() => {
		dictionaryPlatformsTypes();
		// eslint-disable-next-line
	}, []);

	// Convert platform types to proper list and add default value (null)
	const convertedPlatformList = platformList.map((platform) => ({
		name: platform,
		value: platform
	}));
	const platformTypes = [
		{
			name: MATERIAL_PLATFORM_TYPES.default,
			value: MATERIAL_PLATFORM_TYPES.default
		},
		...convertedPlatformList
	];

	const { assetData, assetsFormats } = convertAssets({
		assets,
		assetId,
		select_types
	});

	// Submit function that sends different requests to different urls depending on isEdit flag
	const handleOnSubmit = async (values) => {
		try {
			const options = createMaterialOptions(values, assetData);

			const url = `/player/video/${
				isEdit ? `asset/${assetId}/update` : `${id}/asset/create`
			}`;

			// sending request with form data
			const { data } = await services.post(url, options);
			// updating data in Redux, so new data is immidately displayed in table without sending additional requests
			updateData(data, type, id);

			// close modal
			toggleFormModal();
			// show success notification
			createFormNotification(!isEdit, 'asset');

			// if we are editing, set isEditFlag to off and reset id of edited asset in contex state
			if (isEdit) {
				toggleEdit();
				setAssetId(null);
			}
		} catch (error) {
			// display error notification to client
			createFormNotification(
				!isEdit,
				'asset',
				'error',
				'materials_form_error_message',
				10
			);
		}
	};

	const initialValues = createInitialValues(isEdit, assetData, assetsFormats);

	// drmKey passed to fields to be displayed as placeholder
	const isDrmKey = isEdit ? assetData.drm_key : null;

	return (
		<Form
			onSubmit={handleOnSubmit}
			validate={validate}
			initialValues={initialValues}
		>
			{({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit}>
					<Fields drmKey={isDrmKey} platformTypes={platformTypes} />
					<FormGroup>
						<Col
							className="d-flex justify-content-end m-t-md"
							sm={{ size: '10', offset: 2 }}
						>
							<Button type="submit" disabled={submitting}>
								{t('common:buttons.submit')}
							</Button>
						</Col>
					</FormGroup>
				</form>
			)}
		</Form>
	);
};

const mapStateToProps = ({ dictionary: { platformTypes } }) => {
	return {
		platformList: platformTypes
	};
};

MaterialsForm.propTypes = {
	id: PropTypes.number,
	type: PropTypes.string,
	assets: PropTypes.array,
	dictionaryPlatformsTypes: PropTypes.func,
	platformList: PropTypes.array
};

export default connect(mapStateToProps, { dictionaryPlatformsTypes })(
	MaterialsForm
);
